// src/screens/LoginEmail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiMail } from "react-icons/fi";
import { useAuth } from "../auth/AuthProvider";

function isValidEmail(v) {
  const s = String(v || "").trim().toLowerCase();
  return s.includes("@") && s.includes(".") && s.length >= 6;
}

export default function LoginEmail() {
  const nav = useNavigate();
  const { requestPin, isAuthed } = useAuth();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);
  const [focused, setFocused] = useState(false);

  const emailNorm = useMemo(
    () => String(email || "").trim().toLowerCase(),
    [email]
  );
  const canSubmit = isValidEmail(emailNorm) && !loading;

  useEffect(() => {
    if (isAuthed) nav("/today", { replace: true });
  }, [isAuthed, nav]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setSent(false);

    if (!isValidEmail(emailNorm)) {
      setErr("E-mail inválido.");
      return;
    }

    setLoading(true);
    try {
      await requestPin(emailNorm);
      setSent(true);
      nav("/login/otp", { replace: true, state: { email: emailNorm } });
    } catch (e2) {
      setErr(String(e2?.message || "Falha ao enviar PIN"));
    } finally {
      setLoading(false);
    }
  }

  const S = styles;

  return (
    <div style={S.page}>
      {/* background */}
      <div style={S.bgNoise} />
      <div style={S.bgGlow1} />
      <div style={S.bgGlow2} />

      <div style={S.shell}>
        <div style={S.card}>
          {/* subtle highlight */}
          <div style={S.cardHighlight} />

          {/* Brand block */}
          <div style={S.brandRow}>
            <div style={S.brandIconWrap}>
              <img
                src="/logo-clear.png"
                alt="FinBot"
                style={S.brandIcon}
                draggable={false}
              />
            </div>

            <div style={{ flex: 1 }}>
              <div style={S.brandName}>FinBot</div>
              <div style={S.brandTagline}>
                Seu copiloto de rotina de investimento.
              </div>
            </div>
          </div>

          <div style={S.header}>
            <div style={S.title}>Entrar</div>
            <div style={S.subtitle}>
              Digite seu e-mail para receber um PIN temporário.
            </div>
          </div>

          <form onSubmit={onSubmit} style={S.form}>
            <label style={S.label}>E-mail</label>

            <div
              style={{
                ...S.inputWrap,
                ...(focused ? S.inputWrapFocused : null),
                ...(err ? S.inputWrapError : null),
              }}
            >
              <div style={S.icon}>
                <FiMail />
              </div>

              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="email"
                autoComplete="email"
                placeholder="seuemail@dominio.com"
                style={S.emailInput}
              />
            </div>

            {err ? (
              <div style={S.error}>{err}</div>
            ) : sent ? (
              <div style={S.sent}>PIN enviado. Abrindo verificação…</div>
            ) : (
              <div style={S.helper}>
                Dica: confira Spam/Promoções se não aparecer em alguns minutos.
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                ...S.primaryBtn,
                ...(canSubmit ? S.primaryBtnEnabled : S.primaryBtnDisabled),
              }}
            >
              <span style={S.primaryBtnText}>
                {loading ? "Enviando..." : "Enviar PIN"}
              </span>
              {!loading ? <FiArrowRight /> : null}
            </button>

            <div style={S.disclaimer}>
              Ao continuar, você confirma que o FinBot não executa operações financeiras.
            </div>
          </form>
        </div>
      </div>

    </div>
  );
}

const styles = {
  page: {
    minHeight: "100dvh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    padding: 16,
    paddingTop: "max(18px, env(safe-area-inset-top))",
    paddingBottom: "max(18px, env(safe-area-inset-bottom))",
    boxSizing: "border-box",
    background:
      "radial-gradient(1200px 700px at 15% -10%, rgba(255,255,255,0.10), rgba(0,0,0,0) 55%), radial-gradient(900px 600px at 85% 120%, rgba(255,255,255,0.07), rgba(0,0,0,0) 60%)",
  },

  // leve “grain” fake (fica sofisticado sem imagem)
  bgNoise: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(rgba(255,255,255,0.02), rgba(255,255,255,0.02))",
    opacity: 0.35,
    pointerEvents: "none",
  },
  bgGlow1: {
    position: "absolute",
    left: "-260px",
    top: "-240px",
    width: 620,
    height: 620,
    borderRadius: 9999,
    background: "radial-gradient(circle, rgba(255,255,255,0.10), rgba(0,0,0,0) 60%)",
    filter: "blur(1px)",
    pointerEvents: "none",
  },
  bgGlow2: {
    position: "absolute",
    right: "-260px",
    bottom: "-260px",
    width: 700,
    height: 700,
    borderRadius: 9999,
    background: "radial-gradient(circle, rgba(255,255,255,0.08), rgba(0,0,0,0) 62%)",
    filter: "blur(1px)",
    pointerEvents: "none",
  },

  shell: {
    minHeight:
      "calc(100dvh - max(18px, env(safe-area-inset-top)) - max(18px, env(safe-area-inset-bottom)))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 58, // espaço pro rodapé
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1,
  },

  card: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 22,
    padding: 18,
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.10)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
    boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    position: "relative",
    overflow: "hidden",
  },

  cardHighlight: {
    position: "absolute",
    inset: "-40% -20% auto -20%",
    height: 220,
    background:
      "radial-gradient(closest-side, rgba(255,255,255,0.10), rgba(0,0,0,0))",
    pointerEvents: "none",
  },

  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    position: "relative",
    zIndex: 1,
    marginBottom: 14,
  },
  brandIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  },
  brandIcon: {
    width: 40,
    height: 40,
    opacity: 0.95,
  },
  brandName: {
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: 0.2,
  },
  brandTagline: {
    marginTop: 2,
    fontSize: 13,
    opacity: 0.70,
    lineHeight: 1.2,
  },

  header: {
    position: "relative",
    zIndex: 1,
    marginBottom: 14,
  },
  title: {
    fontSize: 22,
    fontWeight: 900,
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  subtitle: {
    opacity: 0.78,
    lineHeight: 1.35,
    fontSize: 14,
  },

  form: { width: "100%", boxSizing: "border-box", position: "relative", zIndex: 1 },
  label: {
    display: "block",
    marginTop: 6,
    marginBottom: 8,
    opacity: 0.85,
    fontSize: 13,
  },

  inputWrap: {
    position: "relative",
    width: "100%",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.20)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
  },
  inputWrapFocused: {
    border: "1px solid rgba(255,255,255,0.26)",
    boxShadow:
      "0 0 0 4px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  inputWrapError: {
    border: "1px solid rgba(255,107,107,0.60)",
    boxShadow: "0 0 0 4px rgba(255,107,107,0.10)",
  },

  icon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    opacity: 0.70,
    pointerEvents: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emailInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 12px 13px 42px",
    borderRadius: 14,
    border: "none",
    background: "transparent",
    color: "white",
    outline: "none",
    fontSize: 15,
    overflowWrap: "anywhere",
  },

  error: { marginTop: 10, color: "#ff6b6b", fontSize: 13 },
  sent: { marginTop: 10, color: "rgba(255,255,255,0.78)", fontSize: 13 },
  helper: { marginTop: 10, opacity: 0.65, fontSize: 13 },

  primaryBtn: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 16,
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    userSelect: "none",
    transition: "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
  },
  primaryBtnEnabled: {
    background: "linear-gradient(180deg, rgba(255,255,255,1), rgba(230,230,230,1))",
    color: "black",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 14px 34px rgba(0,0,0,0.35)",
  },
  primaryBtnDisabled: {
    background: "rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.70)",
    fontWeight: 900,
    cursor: "not-allowed",
    boxShadow: "none",
    opacity: 0.70,
  },
  primaryBtnText: { lineHeight: 1 },

  disclaimer: {
    marginTop: 12,
    fontSize: 12,
    lineHeight: 1.35,
    opacity: 0.55,
  },

  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "max(10px, env(safe-area-inset-bottom))",
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 2,
  },
  mbLogo: {
    height: 30,
    width: "auto",
    opacity: 0.75,
    filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.50))",
  },
};
